import * as React from "react";
import { useParams, useNavigate } from "react-router-dom";
import MultipleMedicalCarriersDialog from "../../components/MultipleMedicalCarriersDialog";
import AetnaRenewalNoticeModal from "../../components/AetnaRenewalNoticeModal";
import useFeatureAccess from "../../hooks/useFeatureAccess";
import {
  isAetnaNoticeDismissed,
  markAetnaNoticeDismissed,
} from "../../utils/aetnaRenewalNotice";
import { FEATURE_KEYS } from "../../constants";
import {
  useQueryGetClientRateModelSubmitCarrierRule,
  isBlockedBySubmitCarrierRule,
} from "../../data/services/featureAccessService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faUserGroup,
  faFileInvoiceDollar,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartLegend,
  ChartSeriesLabels,
} from "@progress/kendo-react-charts";
import "hammerjs";
import { Button } from "@progress/kendo-react-buttons";
import LoadingIndicator from "../../components/LoadingIndicator";
import { formatNumber } from "@progress/kendo-intl";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Breadcrumb } from "@progress/kendo-react-layout";
import AddModelVersion from "./AddModelVersion";
import ClientRateModelSummary from "./ClientRateModelSummary";
import ClientRates from "./ClientRates";
import AlertDialog from "../../components/AlertDialog";
import { useMutateGetStarted } from "../../data/services/clientService";
import DisclosureModal from "./DisclosureModal";
import ThankYouModal from "./ThankYouModal";
import { setRateGridFields } from "./ClientRateModelHelper";
import {
  hasAnyTermedPlans,
  calcNewPremiumTotal,
  calcRateChangePercentOrNa,
  dedupDeletedTermedPlansByPlan,
  NOT_AVAILABLE,
} from "./ClientTerminationHelper";
import TerminatedBadge, {
  TERMINATED_COLOR,
} from "../../components/TerminatedBadge";
import { Loader } from "@progress/kendo-react-indicators";

import {
  useQueryGetClient,
  useQueryGetModelVersions,
  useQueryGetModelSummary,
  useQueryGetModelDetail,
  useQueryGetClientBenefitRep,
  useQueryGetClientSalesRep,
  useQueryGetClientPlans,
} from "../../data/services/clientService";
import UserContext from "../../layouts/UserContext";
import OEContext from "../../layouts/OEContext";
import { PRIVILEGE_TYPE } from "../../constants/index";

const prepareData = (data) => {
  return data.flatMap((group) =>
    group.rates.map((rate) => {
      let newMonthlyPremium = rate.newTotalPremium * rate.eeCount;
      let currentMonthlyPremium = rate.currentTotalPremium * rate.eeCount;
      let newAnnualPremium = rate.newTotalPremium * rate.eeCount * 12; // assuming monthly premium
      let currentAnnualPremium = rate.currentTotalPremium * rate.eeCount * 12;
      return {
        id: `${group.groupName}-${rate.clientRateModelId}`,
        groupName: group.groupName,
        planName: rate.planName,
        planType: rate.planType,
        planTypeDescription: rate.planTypeDescription,
        currentTotalPremium: rate.currentTotalPremium,
        newTotalPremium: rate.newTotalPremium,
        eeCount: rate.eeCount,
        newMonthlyPremium,
        currentMonthlyPremium,
        newAnnualPremium,
        currentAnnualPremium,
        isTermed: rate.isTermed === true,
        terminationType: rate.terminationType || null,
        isCarrierTerminated: rate.isCarrierTerminated === true,
        percentageChange:
          currentMonthlyPremium === 0
            ? newMonthlyPremium === 0
              ? 0
              : 100
            : ((newMonthlyPremium - currentMonthlyPremium) /
                currentMonthlyPremium) *
              100,
      };
    }),
  );
};

const prepareTotals = (data) => {
  let totalPremium = 0;
  for (let rate of data) {
    totalPremium += rate.newTotalPremium * rate.eeCount; // assuming eeCount is the number of employees
  }
  return totalPremium;
};

const ClientSummary = () => {
  const { clientId, oePeriodId } = useParams();

  // Aetna feature gate, for the one-time renewal notice below. The carrier
  // submission rule no longer reads this - the server owns that verdict.
  const hasAetnaFeature = useFeatureAccess(
    FEATURE_KEYS.Aetna,
    oePeriodId,
    clientId,
  );
  const [
    showMultipleMedicalCarriersModal,
    setShowMultipleMedicalCarriersModal,
  ] = React.useState(false);
  const navigate = useNavigate();
  const { loggedInUser } = React.useContext(UserContext);
  const oePeriod = React.useContext(OEContext);
  const useMutateGetStartedHandler = useMutateGetStarted();

  // One-time Aetna renewal notice, for client contacts and brokers only.
  // `hasClientAppPrivilege` is also true for admin and BDM/sales users - and
  // /admin/clients/.../summary renders this very component - so both are
  // excluded explicitly.
  const [showAetnaNotice, setShowAetnaNotice] = React.useState(false);
  const isClientOrBrokerUser =
    !!loggedInUser?.hasClientAppPrivilege &&
    !loggedInUser?.hasAdminAppPrivilege &&
    !loggedInUser?.bdmUserPrivilege;

  React.useEffect(() => {
    // useFeatureAccess is false while the lookup is in flight and on error, so
    // this stays closed until the gate actually resolves to true.
    if (!hasAetnaFeature || !isClientOrBrokerUser) {
      return;
    }

    if (isAetnaNoticeDismissed(loggedInUser?.userId)) {
      return;
    }

    setShowAetnaNotice(true);
  }, [hasAetnaFeature, isClientOrBrokerUser, loggedInUser?.userId]);

  const handleAetnaNoticeClose = () => {
    markAetnaNoticeDismissed(loggedInUser?.userId);
    setShowAetnaNotice(false);
  };

  const [hasDeletedPlan, setHasDeletedPlan] = React.useState(false);
  const [isTermedPlansExpanded, setIsTermedPlansExpanded] =
    React.useState(false);

  const [clientBenefitRepLocal, setClientBenefitRepLocal] = React.useState();
  const [clientSalesRepLocal, setClientSalesRepLocal] = React.useState();
  const [client, setClient] = React.useState({});
  const [clientStatus, setClientStatus] = React.useState("");
  const [currentModelVersionId, setCurrentModelVersionId] =
    React.useState(null);

  const [showThankYou, setShowThankYou] = React.useState(false);

  const [showSubmitErrorModel, setShowSubmitErrorModel] = React.useState(false);

  const [newModelVersionId, setNewModelVersionId] = React.useState(null);

  // Carrier submission verdict for the version being submitted. Same server-owned
  // rule as the rate model screen; see ClientRateModelLayout for the rationale.
  const submitCarrierRuleQuery = useQueryGetClientRateModelSubmitCarrierRule(
    oePeriodId,
    clientId,
    newModelVersionId,
  );
  const submitCarrierRule = submitCarrierRuleQuery.data?.data;
  const isCarrierSubmitBlocked = isBlockedBySubmitCarrierRule(
    submitCarrierRuleQuery,
  );
  const [showSubmitConfirmModel, setShowSubmitConfirmModel] =
    React.useState(false);
  const [isWelcomeMessage, setIsWelcomeMessage] = React.useState(false);
  const [isClientWelcomeMessage, setIsClientWelcomeMessage] =
    React.useState(false);
  const [currentModelSummaryData, setCurrentModelSummaryData] =
    React.useState();
  const [newModelSummaryData, setNewModelSummaryData] = React.useState();

  const [showNewModelVersionDialog, setShowNewModelVersionDialog] =
    React.useState(false);
  const [canCreateNewModelVersion, setCanCreateNewModelVersion] =
    React.useState(false);
  const [modelVersionsSrc, setModelVersionsSrc] = React.useState([]);

  const [newModelDetailData, setNewModelDetailData] = React.useState();
  const [newTotalPremiums, setNewTotalPremiums] = React.useState(0);

  const handleDisclosureAccept = () => {
    setShowSubmitConfirmModel(false);
    setShowThankYou(true);
  };

  const handleThankYouClose = () => {
    setShowThankYou(false);
  };

  const { isLoading: clientLoading, data: clientResponse } = useQueryGetClient(
    oePeriodId,
    clientId,
  );
  const { isLoading: modelVersionsLoading, data: modelVersions } =
    useQueryGetModelVersions(oePeriodId, clientId);
  const { isLoading: currentModelSummaryLoading, data: currentModelSummary } =
    useQueryGetModelSummary(oePeriodId, clientId, currentModelVersionId);
  const {
    isLoading: newModelSummaryLoading,
    data: newModelSummary,
    refetch: refetchModelSummery,
  } = useQueryGetModelSummary(
    oePeriodId,
    clientId,
    newModelVersionId,
    currentModelVersionId,
  );
  const {
    isLoading: newModelDetailLoading,
    data: newModelDetail,
    refetch: refetchModelDetail,
  } = useQueryGetModelDetail(oePeriodId, clientId, newModelVersionId);
  const { isLoading: clientBenefitRepLoading, data: clientBenefitRep } =
    useQueryGetClientBenefitRep(clientId);
  const { isLoading: clientSalesRepLoading, data: clientSalesRep } =
    useQueryGetClientSalesRep(clientId);
  const { isLoading: clientRatesLoading, data: clientRates } =
    useQueryGetClientPlans(oePeriodId, clientId);

  const isRateSubmitted = React.useMemo(
    () =>
      client.oeStatus === "Submitted" ||
      client.oeStatus === "PublishInProgress" ||
      client.oeStatus === "PublishError" ||
      client.oeStatus === "Published" ||
      client.oeStatus === "PublishedDefault",
    [client],
  );

  React.useEffect(() => {
    if (!modelVersionsLoading && modelVersions && modelVersions.data) {
      setModelVersionsSrc(
        modelVersions.data.filter((e) => e.isSystem !== true),
      );
      if (modelVersions.data.length > 0) {
        let current = modelVersions.data.find((e) => e.isSystem === true);
        if (current) {
          setCurrentModelVersionId(current.modelVersionId);
        }

        if (isRateSubmitted) {
          setNewModelVersionId(client.submittedVersionId);
        } else if (!newModelVersionId) {
          let newModel = modelVersions.data.find(
            (e) => e.isClientDefault === true,
          );
          if (newModel) {
            setNewModelVersionId(newModel.modelVersionId);
          } else {
            let newModel = modelVersions.data.find((e) => !e.isSystem);
            if (newModel) {
              setNewModelVersionId(newModel.modelVersionId);
            }
          }
        }
      }
      let gridData =
        clientRates && clientRates.data && clientRates.data.groups
          ? prepareData(clientRates.data.groups)
          : [];
      if (gridData && gridData.length > 0) {
        setNewTotalPremiums(calcNewPremiumTotal(gridData));
        setHasDeletedPlan(hasAnyTermedPlans(gridData));
      }
      setCanCreateNewModelVersion(modelVersions.data.length < 4);
    }

    if (
      !currentModelSummaryLoading &&
      currentModelSummary &&
      currentModelSummary.data
    ) {
      setCurrentModelSummaryData(currentModelSummary.data);
    }

    if (!newModelSummaryLoading && newModelSummary && newModelSummary.data) {
      setNewModelSummaryData(newModelSummary.data);
    }

    if (!newModelDetailLoading && newModelDetail && newModelDetail.data) {
      setRateGridFields(newModelDetail);

      setNewModelDetailData(newModelDetail.data);
    }
  }, [
    modelVersionsLoading,
    modelVersions,
    currentModelSummaryLoading,
    currentModelSummary,
    newModelSummaryLoading,
    newModelSummary,
    newModelVersionId,
    newModelDetailLoading,
    newModelDetail,
    clientRates,
    clientRatesLoading,
    client.submittedVersionId,
    isRateSubmitted,
  ]);

  function hasCreatedByBroker(arr) {
    if (arr) {
      return arr.some((item) => item.isBroker === true);
    }
  }
  function hasCreatedByClientAdmin(arr) {
    if (arr) {
      return arr.some((item) => item.isAdmin === true);
    }
  }
  function hasCreatedByClient(arr) {
    if (arr) {
      return arr.some(
        (item) => item.isAdmin === false && item.isBroker === false,
      );
    }
  }

  const onSubmitClick = () => {
    // Carrier feature keys can forbid this combination of medical carriers.
    // Checked first so the carrier rule takes precedence.
    if (isCarrierSubmitBlocked) {
      setShowMultipleMedicalCarriersModal(true);
      return;
    }

    if (newModelDetailData && newModelDetailData.groups) {
      let hasMissingData = newModelDetailData.groups.some((group) =>
        group.rates.some((e) => !e.percentSelected && !e.fixedValueSelected),
      );

      if (hasMissingData) {
        setShowSubmitErrorModel(true);
      } else {
        setShowSubmitConfirmModel(true);
      }
    }
  };

  React.useEffect(() => {
    if (!!modelVersionsSrc && !!loggedInUser) {
      // Check if the user has the BrokerUserPrivilege
      const BrokerUserPrivilege = loggedInUser?.privileges.includes(
        PRIVILEGE_TYPE.BrokerUserPrivilege,
      );

      // Check for broker and client messages based on user privileges and source data
      if (BrokerUserPrivilege) {
        if (hasCreatedByBroker(modelVersionsSrc)) {
          setIsWelcomeMessage(false);
        } else if (hasCreatedByClientAdmin(modelVersionsSrc)) {
          setIsWelcomeMessage(true);
        }
      } else if (loggedInUser?.hasClientAppPrivilege) {
        if (
          hasCreatedByBroker(modelVersionsSrc) &&
          !hasCreatedByClient(modelVersionsSrc)
        ) {
          // changed this to false from true 9/3/2025 since clients need to be able to edit if broker created the model
          setIsWelcomeMessage(false);
        } else if (hasCreatedByClient(modelVersionsSrc)) {
          setIsWelcomeMessage(false);
        } else if (hasCreatedByClientAdmin(modelVersionsSrc)) {
          if (
            client.oeStatus === "NotStarted" &&
            loggedInUser.hasClientAppPrivilege
          ) {
            setIsWelcomeMessage(true);
            setIsClientWelcomeMessage(true);
          } else {
            setIsWelcomeMessage(false);
          }
        }
      }
    } else {
    }
  }, [client.oeStatus, loggedInUser, modelVersionsSrc]);

  React.useEffect(() => {
    if (!clientLoading && clientResponse && clientResponse.data) {
      const clientData = clientResponse.data;
      setClient(clientData);
      setClientStatus(clientData.oeStatus);
    } else if (!clientLoading && clientResponse && !clientResponse.data) {
      if (loggedInUser.hasAdminAppPrivilege) {
        navigate("/admin/clientadminsummary");
      } else {
        navigate("/dashboard");
      }
    }
  }, [clientLoading, clientResponse, navigate, loggedInUser]);

  React.useEffect(() => {
    if (clientBenefitRepLoading || !clientBenefitRep) {
      return;
    }
    if (newModelVersionId) {
      refetchModelDetail();
    }
    setClientBenefitRepLocal(clientBenefitRep.data);
  }, [clientBenefitRepLoading, clientBenefitRep, newModelVersionId, refetchModelDetail]);

  React.useEffect(() => {
    if (clientSalesRepLoading || !clientSalesRep) {
      return;
    }
    setClientSalesRepLocal(clientSalesRep.data);
  }, [clientSalesRepLoading, clientSalesRep]);

  // The client's own personnel, and nobody else. hasClientAppPrivilege only
  // means "can use the client app" and is carried by brokers, sales users and
  // internal admins too, which is why the server checks IsClientContact
  // rather than IsClientAdmin for anything that is a client's decision.
  const isClientContact = !!loggedInUser?.privileges?.includes(
    PRIVILEGE_TYPE.ContactUserPrivilege,
  );

  if (modelVersionsLoading || currentModelSummaryLoading || clientLoading) {
    return <LoadingIndicator />;
  }

  if (!client) {
    return <div>Client not found</div>;
  }

  const handleCreateModel = async () => {
    setShowNewModelVersionDialog(true);
  };

  const handleGetStarted = async (item) => {
    let data = {
      clientId: clientId,
      oePeriodId: oePeriodId,
    };

    try {
      let response = await useMutateGetStartedHandler.mutateAsync(data);
      if (response && response.data) {
        setIsWelcomeMessage(false);
        setIsClientWelcomeMessage(false);
        setClientStatus("InProgress");
      }
    } catch {
      // Leave the status as NotStarted and the panel up. Showing a submit path
      // the server will reject is worse than leaving the client on a button
      // they can press again.
    }
  };

  const handleClose = () => {
    setIsWelcomeMessage(false);
  };

  const handleModelVersionChange = (e) => {
    setNewModelVersionId(e.target.value.modelVersionId);
    setIsWelcomeMessage(false);
    refetchModelSummery();
    refetchModelDetail();
  };

  const RenderNav = () => {
    let data = [
      {
        id: "home",
        text: loggedInUser.hasAdminAppPrivilege
          ? `${client?.clientId} - ${client?.clientName}`
          : "Home",
        iconClass: "k-i-home",
      },
    ];

    return (
      <div>
        <Breadcrumb data={data} className={"oe-breadcrumb"} />
      </div>
    );
  };

  const RenderVersionBar = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "#fff",
          marginBottom: 0,
        }}
      >
        <DropDownList
          style={{
            width: "250px",
            marginRight: 20,
          }}
          data={modelVersionsSrc}
          fillMode={"flat"}
          textField="modelName"
          dataItemKey="modelVersionId"
          value={modelVersionsSrc.find(
            (x) => x.modelVersionId === newModelVersionId,
          )}
          onChange={handleModelVersionChange}
          loading={modelVersionsLoading}
        />

        <div style={{ marginTop: 2, marginRight: 20 }}>
          {newModelDetailLoading && <Loader size="small" type={"pulsing"} />}
          {!newModelDetailLoading &&
            newModelDetailData &&
            newModelDetailData.creatorUserName && (
              <span
                style={{
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  paddingTop: "1px",
                  paddingBottom: "1px",
                  backgroundColor: "grey",
                  borderRadius: "15px",
                  color: "white",
                  fontSize: "12px",
                }}
              >
                Created by {newModelDetailData.creatorUserName}
              </span>
            )}
        </div>

        {!loggedInUser?.salesAdminPrivilege && (
          <Button
            themeColor="primary"
            onClick={handleCreateModel}
            disabled={!canCreateNewModelVersion}
          >
            Create New Contribution Model
          </Button>
        )}
      </div>
    );
  };

  const RenderClientSummary = ({
    title,
    data,
    showViewButton,
    isCurrent,
    currentData,
    isWelcomeMessage,
    isClientWelcomeMessage,
  }) => {
    const hasClientModelVersions = modelVersionsSrc.length > 0;
    // A client contact who did not build their own model had no path out of
    // NotStarted: the panel below used to render only when no model existed
    // (!hasClientModelVersions), and the only other writer of InProgress is
    // creating a model yourself. Submit demands exactly InProgress, so such a
    // client could open and edit the model but never submit it - prod client
    // 10360, OE period 6, on a broker-created model. Show them the button.
    //
    // Gated on ContactUserPrivilege rather than hasClientAppPrivilege so this
    // agrees with the server's IsClientContact check. Brokers, sales users and
    // admins all carry hasClientAppPrivilege, and taking the projected figures
    // away from them on a client they are merely viewing is not the intent.
    //
    // !isCurrent is load-bearing. This flag is evaluated for both cost boxes,
    // and unlike isClientWelcomeMessage - which the Current box is protected
    // from simply by never being passed it - a flag read from outer scope
    // would blank out the current figures too.
    const clientMustStart =
      !isCurrent &&
      isClientContact &&
      clientStatus === "NotStarted" &&
      hasClientModelVersions;
    // Sales Admins are read-only: they never see a call-to-action for a
    // control the server will reject (SalesAdminPrivilege gets a 403 on
    // every create/edit/copy/delete/submit endpoint).
    const showClientGetStarted =
      !loggedInUser?.salesAdminPrivilege &&
      (isClientWelcomeMessage ||
        clientMustStart ||
        (!data && !hasClientModelVersions && clientStatus === 'NotStarted' && loggedInUser.hasClientAppPrivilege));
    const showCreateModelCta =
      !loggedInUser?.salesAdminPrivilege &&
      (isWelcomeMessage ||
        (!data && currentData) ||
        (!data && !hasClientModelVersions && clientStatus !== 'NotStarted'));

    if (isCurrent && !data) {
      return (
        <div style={{ margin: 20 }}>
          No current benefit setup was found for this client yet.
        </div>
      );
    } else if (showClientGetStarted) {
      return (
        <div style={{ paddingBottom: 30 }}>
          <div
            style={{
              textAlign: "center",
              marginTop: 100,
              backgroundColor: "#f6f6f6",
              paddingBottom: 20,
              position: "relative",
            }}
          >
            {hasClientModelVersions &&
            (client.oeStatus !== "NotStarted" ||
              !loggedInUser.hasClientAppPrivilege) ? (
              <button
                onClick={handleClose}
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                }}
              >
                &times;
              </button>
            ) : null}
            <div
              style={{
                fontSize: "15px",
                fontWeight: "500",
                marginBottom: "20px",
                padding: 30,
              }}
            >
              <h2>Welcome to {client.oePeriodName}</h2>
              To start building your benefit setup, click the button below.
            </div>
            <Button themeColor="primary" onClick={handleGetStarted}>
              Get Started
            </Button>
          </div>
        </div>
      );
    } else if (showCreateModelCta) {
      return (
        <div style={{ paddingBottom: 30 }}>
          <div
            style={{
              textAlign: "center",
              marginTop: 100,
              backgroundColor: "#f6f6f6",
              paddingBottom: 20,
              position: "relative",
            }}
          >
            {hasClientModelVersions &&
            (client.oeStatus !== "NotStarted" ||
              !loggedInUser.hasClientAppPrivilege) ? (
              <button
                onClick={handleClose}
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                }}
              >
                &times;
              </button>
            ) : null}
            <div
              style={{
                fontSize: "15px",
                fontWeight: "500",
                marginBottom: "20px",
                padding: 30,
              }}
            >
              <h2>Welcome to {client.oePeriodName}</h2>
              Create a contribution model to set up plan offerings and compare
              options before submitting.
            </div>
            <Button themeColor="primary" onClick={handleCreateModel}>
              Create New Contribution Model
            </Button>
          </div>
        </div>
      );
    } else if (!data) {
      return <div style={{ margin: 20 }}>NA</div>;
    }

    const renderSummary = (
      icon,
      contribution,
      label,
      percentageIncreaseText,
    ) => {
      return (
        <>
          <div
            style={{
              float: "left",
              height: "40px",
              padding: "10px",
              fontSize: "1.5rem",
            }}
          >
            <FontAwesomeIcon icon={icon} style={{ verticalAlign: "middle" }} />
          </div>
          <div style={{ marginTop: "10px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "5px",
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: "20px" }}>
                {formatNumber(contribution, "c")}
              </div>
              {percentageIncreaseText}
            </div>
            <div style={{ fontSize: "10px", color: "#7D807D" }}>{label}</div>
          </div>
        </>
      );
    };

    const renderChartAxisLabel = (data) => {
      return `${data.value.planOfferTypeDesc}\n(${data.value.eeCount} members)`;
    };

    const renderLabelContent = (data) => {
      return formatNumber(data.value, "c");
    };

    const renderChart = (data, header, companyField, empField) => {
      return (
        <div style={{ marginBottom: "20px" }}>
          <fieldset
            style={{
              border: "1px solid lightgrey",
              borderRadius: "3px",
              padding: 0,
            }}
          >
            <legend>
              <b style={{ fontSize: 13 }}>{header}</b>
            </legend>

            <div style={{ margin: "5px" }}>
              <Chart style={{ height: "220px", width: "100%" }} transitions={false}>
                <ChartLegend position="bottom" orientation="horizontal" />
                <ChartCategoryAxis>
                  <ChartCategoryAxisItem
                    categories={data.totalCosts}
                    line={{ visible: false }}
                    majorGridLines={{ visible: false }}
                    minorGridLines={{ visible: false }}
                    labels={{ content: renderChartAxisLabel }}
                  ></ChartCategoryAxisItem>
                </ChartCategoryAxis>
                <ChartValueAxis>
                  <ChartValueAxisItem
                    majorGridLines={{ visible: false }}
                    visible={false}
                    minorGridLines={{ visible: false }}
                  ></ChartValueAxisItem>
                </ChartValueAxis>
                <ChartSeries>
                  <ChartSeriesItem
                    name={"Company Contribution"}
                    type="bar"
                    gap={1}
                    spacing={0.5}
                    data={data.totalCosts}
                    field={companyField}
                    color={"#40D555"}
                    border={{ color: "#40D555" }}
                    autoFit={true}
                    labels={{ visible: true }}
                  >
                    <ChartSeriesLabels content={renderLabelContent} />
                  </ChartSeriesItem>
                  <ChartSeriesItem
                    name={"Employee Contribution"}
                    type="bar"
                    data={data.totalCosts}
                    field={empField}
                    color={"#339DFF"}
                    border={{ color: "#339DFF" }}
                    autoFit={true}
                    labels={{ visible: true }}
                  >
                    <ChartSeriesLabels content={renderLabelContent} />
                  </ChartSeriesItem>
                </ChartSeries>
              </Chart>
            </div>
          </fieldset>
        </div>
      );
    };

    const onViewEditModelClick = () => {
      if (loggedInUser.hasAdminAppPrivilege) {
        navigate(
          `/admin/clients/${clientId}/oe/${oePeriodId}/ratemodel/${newModelVersionId}`,
        );
      } else {
        navigate(
          `/clients/${clientId}/oe/${oePeriodId}/ratemodel/${newModelVersionId}`,
        );
      }
    };

    const onViewPlanComparisonsClick = () => {
      if (loggedInUser.hasAdminAppPrivilege) {
        navigate(`/admin/clients/${clientId}/oe/${oePeriodId}/menucomparisons`);
      } else {
        navigate(`/clients/${clientId}/oe/${oePeriodId}/menucomparisons`);
      }
    };

    // `previous` is always the current box's own displayed total, so the % matches the
    // dollar amounts shown on screen. The comparisonXxxContribution fields (system model's
    // rates priced at the projected model's EECount) were tried as the baseline instead,
    // but they silently drop any plan that doesn't key-match between the two models (e.g.
    // a renewal replacing a plan with a new plan-year PlanId) to $0 rather than omitting it
    // consistently, which can shrink the baseline far below the displayed total and produce
    // wildly inflated percentages.
    const getPercentageIncreaseText = (show, previous, current) => {
      if (!show) {
        return null;
      }

      // SOF-826: when terminated plans are present, the % change would rely on premium
      // data that is no longer reliable, so show N/A rather than a misleading rate change.
      const termedAffectsRollup =
        (clientRates?.data?.deletedPlans?.length ?? 0) > 0 ||
        (clientRates?.data?.termedPlans?.length ?? 0) > 0;

      // calcRateChangePercentOrNa returns null (=> N/A) on a missing or zero baseline,
      // which is exactly the unreliable case; never fabricate 100% or $0-based values.
      const change = termedAffectsRollup
        ? null
        : calcRateChangePercentOrNa(previous, current);

      if (change === null || change === undefined) {
        return (
          <div
            style={{ fontSize: "12px", paddingRight: "5px", color: "black" }}
          >
            {NOT_AVAILABLE}
          </div>
        );
      }

      if (change === 0) {
        return (
          <div
            style={{ fontSize: "12px", paddingRight: "5px", color: "black" }}
          >
            0%
          </div>
        );
      }

      const formattedChange = change.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        style: "percent",
      });
      const iconClass = change > 0 ? "red-icon" : "green-icon";
      const arrowIcon = change > 0 ? "▲" : "▼";

      return (
        <div style={{ fontSize: "12px", paddingRight: "5px" }}>
          <span className={iconClass}>
            {arrowIcon}
            {formattedChange}
          </span>
        </div>
      );
    };

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <h5 style={{ marginTop: "15px" }}>{title}</h5>
          </div>
          <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
            {showViewButton && (
              <>
                <Button fillMode="outline" onClick={onViewPlanComparisonsClick}>
                  View Plan Comparisons
                </Button>
                {/* A Sales Admin never sees this button, submitted or not:
                    ClientRateModelLayout unconditionally redirects a Sales
                    Admin away from the model view/edit route, so showing
                    "View Contribution Model" here just flashes a spinner and
                    bounces them back. */}
                {!loggedInUser?.salesAdminPrivilege && (
                  <Button themeColor="info" onClick={onViewEditModelClick}>
                    {isRateSubmitted
                      ? "View Contribution Model"
                      : "Edit Contribution Model"}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        <div
          style={{
            flexDirection: "row",
            display: "flex",
            marginBottom: "30px",
          }}
        >
          <div
            style={{
              width: "33%",
              backgroundColor: "#f6f6f6",
              color: "#40D555",
              borderRadius: "5px",
              marginRight: "10px",
              height: "60px",
            }}
          >
            {renderSummary(
              faBuilding,
              data.companyContribution,
              "Company Contribution",
              getPercentageIncreaseText(
                !isCurrent && currentData,
                currentData?.companyContribution,
                data.companyContribution,
              ),
            )}
          </div>
          <div
            style={{
              width: "33%",
              backgroundColor: "#f6f6f6",
              color: "#339DFF",
              borderRadius: "5px",
              marginRight: "10px",
              height: "60px",
            }}
          >
            {renderSummary(
              faUserGroup,
              data.employeeContribution,
              "Employee Contribution",
              getPercentageIncreaseText(
                !isCurrent && currentData,
                currentData?.employeeContribution,
                data.employeeContribution,
              ),
            )}
          </div>
          <div
            style={{
              width: "33%",
              backgroundColor: "#f6f6f6",
              borderRadius: "5px",
              height: "60px",
            }}
          >
            {renderSummary(
              faFileInvoiceDollar,
              data.totalContribution || newTotalPremiums,
              "Total Contribution",
              getPercentageIncreaseText(
                !isCurrent && currentData,
                currentData?.totalContribution,
                data.totalContribution || newTotalPremiums,
              ),
            )}
          </div>
        </div>
        {renderChart(
          data,
          "Total costs",
          "companyContribution",
          "employeeContribution",
        )}
      </div>
    );
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "#fff",
          marginBottom: 0,
          marginTop: 20,
        }}
      >
        <div>
          <RenderNav />
        </div>
        <div>
          {/* Withholding the picker until the renewal starts is a rule about
              the client's own contacts, who reach InProgress through Get
              Started or the auto-start effect. It must not catch brokers,
              sales users or admins: they all carry hasClientAppPrivilege, so
              gating on that flag hid the picker and the create button from
              whoever had just built the first model, and kept them hidden
              until a contact entered and moved the renewal off NotStarted. */}
          {!isRateSubmitted &&
            modelVersionsSrc.length > 0 &&
            (clientStatus !== "NotStarted" || !isClientContact) && (
              <RenderVersionBar />
            )}
        </div>
      </div>
      {clientBenefitRepLocal && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "#fff",
            marginBottom: 10,
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <div>
            <span style={{ marginLeft: 10 }}>
              <b>Benefit Rep:</b> {clientBenefitRepLocal.userName}
            </span>
            <span style={{ marginLeft: 30 }}>
              <b>Email:</b> {clientBenefitRepLocal.email || "N/A"}
            </span>
            <span style={{ marginLeft: 30 }}>
              <b>Ph:</b> {clientBenefitRepLocal.phone || "N/A"}
            </span>
          </div>
          <div></div>
        </div>
      )}
      {clientSalesRepLocal && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "#fff",
            marginBottom: 10,
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <div>
            <span style={{ marginLeft: 10 }}>
              <b>Sales Rep:</b> {clientSalesRepLocal?.userName}
            </span>
            <span style={{ marginLeft: 30 }}>
              <b>Email:</b> {clientSalesRepLocal?.email || "N/A"}
            </span>
            <span style={{ marginLeft: 30 }}>
              <b>Ph:</b> {clientSalesRepLocal?.phone || "N/A"}
            </span>
          </div>
          <div></div>
        </div>
      )}
      {/*
        A Sales Admin cannot create a model, so RenderClientSummary's
        showCreateModelCta is false for them by design - which dropped them
        through to its bare "NA" placeholder whenever no projected model
        existed, leaving an empty white box beside Current.
        Hide the projected box in that case and give Current the full width.

        Only when it is EMPTY: the projected figures come from their own query
        and populate whenever anyone - the client, a broker, an admin - has
        built a model. Those are read-only numbers a Sales Admin should still
        see, so this must not become a blanket hide.
      */}
      {(() => {
        const hideProjectedForSalesAdmin =
          Boolean(loggedInUser?.salesAdminPrivilege) && !newModelSummaryData;

        return (
          <div style={{ flexDirection: "row", display: "flex" }}>
            <div
              data-testid="current-cost-box"
              style={{
                width: hideProjectedForSalesAdmin ? "100%" : "50%",
                backgroundColor: "#fff",
                borderRadius: "5px",
                boxShadow: "0 0 4px 0 rgba(0, 0, 0, .1)",
                // Dropping the right margin too, or the full-width box stops
                // 10px short of the edge and looks misaligned rather than full.
                marginRight: hideProjectedForSalesAdmin ? "0" : "10px",
                paddingLeft: "10px",
                paddingRight: "10px",
              }}
            >
              <RenderClientSummary
                title={"Current Benefit Cost Per Month"}
                data={currentModelSummaryData}
                isWelcomeMessage={false}
                isCurrent={true}
              />
            </div>
            {!hideProjectedForSalesAdmin && (
              <div
                data-testid="projected-cost-box"
                style={{
                  width: "50%",
                  backgroundColor: "#fff",
                  borderRadius: "5px",
                  boxShadow: "0 0 4px 0 rgba(0, 0, 0, .1)",
                  marginLeft: "10px",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                }}
              >
                <RenderClientSummary
                  title={"Projected Benefit Cost Per Month"}
                  data={newModelSummaryData}
                  currentData={currentModelSummaryData}
                  showViewButton={true}
                  isWelcomeMessage={isWelcomeMessage}
                  isClientWelcomeMessage={isClientWelcomeMessage}
                />
              </div>
            )}
          </div>
        );
      })()}

      {clientRates?.data?.deletedPlans?.length > 0 && (
        <div
          style={{
            fontSize: 20,
            color: "red",
            marginTop: "25px",
            alignSelf: "center",
            textAlign: "center",
          }}
        >
          % Change is not accurate due to terminated plan premium no longer
          available
        </div>
      )}
      {(() => {
        // Same plan fans out one entry per (group, plan, tier) upstream; collapse for the rollup view.
        const dedupedTermedPlans = dedupDeletedTermedPlansByPlan(
          clientRates?.data?.termedPlans ?? []
        );
        if (dedupedTermedPlans.length === 0) return null;
        return (
        <div style={{ marginTop: "25px" }}>
          <button
            onClick={() => setIsTermedPlansExpanded(!isTermedPlansExpanded)}
          >
            Terminated Plans
          </button>
          <span style={{ marginLeft: "10px" }}>
            {dedupedTermedPlans.length} terminated plan
            {dedupedTermedPlans.length !== 1 ? "s" : ""} (hidden by
            default). Click to view details.
          </span>
          {isTermedPlansExpanded && (
            <div style={{ marginTop: "10px" }}>
              <p>
                These plans are terminated. They are shown for comparison
                context only and are not selectable.
              </p>
              {dedupedTermedPlans.map((plan) => (
                <div key={plan.planId} style={{ marginBottom: "10px" }}>
                  <strong style={{ color: TERMINATED_COLOR }}>
                    {plan.planName}
                  </strong>
                  <TerminatedBadge plain />
                  {plan.hasOriginalPremium && (
                    <div>Original termed premium: {plan.originalPremium}</div>
                  )}
                  {plan.hasComparisonPremium && (
                    <div>Comparison premium: {plan.comparisonPremium}</div>
                  )}
                  {plan.hasReplacementPlan ? (
                    <div>
                      Using mapped replacement premium from{" "}
                      {plan.replacementPlanName}
                    </div>
                  ) : (
                    <div style={{ color: TERMINATED_COLOR }}>
                      No replacement mapped
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        );
      })()}
      <div
        className="ratesummary"
        style={{ marginTop: "20px", marginBottom: "20px" }}
      >
        {ClientRates(clientRates)}
      </div>
      <div
        className="ratesummary"
        style={{ marginTop: "20px", marginBottom: "20px" }}
      >
        {ClientRateModelSummary(newModelDetailData)}

        {!isRateSubmitted &&
          loggedInUser.submitContributionModalPrivilege &&
          !loggedInUser?.salesAdminPrivilege &&
          client.oeStatus !== "NotStarted" && (
            <div
              style={{
                float: "right",
                marginTop: "20px",
                marginBottom: "30px",
              }}
            >
              <Button
                themeColor={"success"}
                onClick={() => onSubmitClick(true)}
                disabled={false}
              >
                {" "}
                Submit Contribution Model{" "}
              </Button>
            </div>
          )}
      </div>

      {showNewModelVersionDialog && (
        <AddModelVersion
          client={client}
          onModelClose={() => setShowNewModelVersionDialog(false)}
        />
      )}
      <DisclosureModal
        clientId={clientId}
        oePeriodId={oePeriodId}
        versionId={newModelVersionId}
        oePeriod={oePeriod}
        clientName={client?.clientName}
        loggedInUser={loggedInUser}
        isOpen={showSubmitConfirmModel}
        onClose={() => setShowSubmitConfirmModel(false)}
        onAccept={() => handleDisclosureAccept(false)}
      />
      {showSubmitErrorModel && (
        <AlertDialog
          title={"Incomplete Model"}
          message={
            "Please enter rate contribution for all plans and classes before submitting."
          }
          onClose={() => setShowSubmitErrorModel(false)}
        />
      )}
      {showMultipleMedicalCarriersModal && (
        <MultipleMedicalCarriersDialog
          carrierNames={submitCarrierRule?.carrierNames}
          message={submitCarrierRule?.message}
          onClose={() => setShowMultipleMedicalCarriersModal(false)}
        />
      )}
      {showAetnaNotice && (
        <AetnaRenewalNoticeModal onClose={handleAetnaNoticeClose} />
      )}
      {showThankYou && <ThankYouModal onClose={handleThankYouClose} />}
    </div>
  );
};

export default ClientSummary;
